import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import Select from "react-select";
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const EditProductionCosting = ({ show, onClose, editData, refreshData }) => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;

    const [itemOptions, setItemOptions] = useState([]);
    const [productOptions, setProductOptions] = useState([]);
    const [selectedItemDetails, setSelectedItemDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});

    const [form, setForm] = useState({
        production_item: null,
        production_productcategory: null,
        production_rmcount: '',
        production_rmcost: '',
        production_lcost: '',
    });

    const [totalCost, setTotalCost] = useState(0);

    useEffect(() => {
        const fetchEditData = async () => {
            if (!editData?.sno) return;
            setLoading(true);
            try {
                const [itemRes, productCatRes, productionCostRes] = await Promise.all([
                    axios.get(`${baseURL}/manage-items/active`),
                    axios.get(`${baseURL}/product-categories/products/active`),
                    axios.get(`${baseURL}/production-cost/${editData.sno}`)
                ]);

                const itemList = itemRes.data.data.map(item => ({
                    value: item.sno,
                    label: item.item_name,
                    full: item
                }));
                setItemOptions(itemList);

                const productList = productCatRes.data.data.map(cat => ({
                    value: cat.sno,
                    label: cat.productcategory_name
                }));
                setProductOptions(productList);

                const data = productionCostRes.data.data;

                setForm({
                    production_item: data.item?.id || null,
                    production_productcategory: data.production_category?.id || null,
                    production_rmcount: data.rmcount,
                    production_rmcost: data.rmcost,
                    production_lcost: data.lcost
                });

                const selectedItem = itemList.find(opt => opt.value === data.item?.id);
                setSelectedItemDetails(selectedItem?.full || null);
            } catch (err) {
                console.error("❌ Error fetching data", err);
                toast.error("Failed to load edit data.");
            } finally {
                setLoading(false);
            }
        };

        fetchEditData();
    }, [editData?.sno]);

    useEffect(() => {
        const rm = parseFloat(form.production_rmcost) || 0;
        const lc = parseFloat(form.production_lcost) || 0;
        setTotalCost(rm + lc);
    }, [form.production_rmcost, form.production_lcost]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (parseFloat(value) < 0) return;
        if (value.length > 10) return;

        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSelectChange = (selected, action) => {
        if (action.name === 'production_item') {
            setForm(prev => ({ ...prev, production_item: selected?.value || null }));
            setSelectedItemDetails(selected?.full || null);
            setErrors(prev => ({ ...prev, production_item: '' }));
        } else if (action.name === 'production_productcategory') {
            setForm(prev => ({ ...prev, production_productcategory: selected?.value || null }));
            setErrors(prev => ({ ...prev, production_productcategory: '' }));
        }
    };

    const handleSubmit = async () => {
        const newErrors = {};
        if (!form.production_item) newErrors.production_item = "Item is required";
        if (!form.production_productcategory) newErrors.production_productcategory = "Product category is required";
        if (!form.production_rmcount) newErrors.production_rmcount = "Raw material count is required";
        if (!form.production_rmcost) newErrors.production_rmcost = "Raw material cost is required";
        if (!form.production_lcost) newErrors.production_lcost = "Labour cost is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            // toast.error("Please fill all required fields");
            return;
        }

        try {
            const payload = {
                production_item: form.production_item,
                production_productcategory: form.production_productcategory,
                production_rmcount: parseFloat(form.production_rmcount),
                production_rmcost: parseFloat(form.production_rmcost),
                production_lcost: parseFloat(form.production_lcost)
            };

            await axios.post(`${baseURL}/production-cost/${editData.sno}`, payload);
            toast.success("✅ Production Cost updated successfully");
            if (typeof refreshData === "function") refreshData();
            onClose();
        } catch (err) {
            console.error("❌ Error updating data", err);
            toast.error("Failed to update production cost.");
        }
    };

    return (
        <Modal show={show} size="lg" onHide={onClose}>
            <div className="modal-content">
                <div className="modal-header" style={{ backgroundColor: '#3ab384' }}>
                    <h3 className="modal-title text-white">Update Production Costing</h3>
                    <Button variant="" type="button" className="close px-6 py-2" onClick={onClose}>
                        <span className="text-white">×</span>
                    </Button>
                </div>

                <div className="modal-body">
                    {loading ? (
                        <p>Loading...</p>
                    ) : (
                        <div className="row mt-4">
                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Item<span className="required">*</span></label>
                                <Select
                                    name="production_item"
                                    placeholder="Select Item"
                                    options={itemOptions}
                                    value={itemOptions.find(i => i.value === form.production_item) || null}
                                    onChange={handleSelectChange}
                                    className={errors.production_item ? 'is-invalid' : ''}
                                />
                                {errors.production_item && <div className="text-danger fs-8">{errors.production_item}</div>}
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Item Category</label>
                                <input type="text" className="form-control" readOnly value={selectedItemDetails?.itemcategory_name || ''} />
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Item Sub Category</label>
                                <input type="text" className="form-control" readOnly value={selectedItemDetails?.itemsubcategory_name || ''} />
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Product Category<span className="required">*</span></label>
                                <Select
                                    name="production_productcategory"
                                    placeholder="Select Product Category"
                                    options={productOptions}
                                    value={productOptions.find(i => i.value === form.production_productcategory) || null}
                                    onChange={handleSelectChange}
                                    className={errors.production_productcategory ? 'is-invalid' : ''}
                                />
                                {errors.production_productcategory && <div className="text-danger fs-8">{errors.production_productcategory}</div>}
                            </div>
{/* 
                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Raw Material Count<span className="required">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.production_rmcount ? 'is-invalid' : ''}`}
                                    name="production_rmcount"
                                    value={form.production_rmcount}
                                    onChange={handleChange}
                                />
                                {errors.production_rmcount && <div className="text-danger fs-8">{errors.production_rmcount}</div>}
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Raw Material Amount<span className="required">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.production_rmcost ? 'is-invalid' : ''}`}
                                    name="production_rmcost"
                                    value={form.production_rmcost}
                                    onChange={handleChange}
                                />
                                {errors.production_rmcost && <div className="text-danger fs-8">{errors.production_rmcost}</div>}
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Labour Cost<span className="required">*</span></label>
                                <input
                                    type="number"
                                    className={`form-control ${errors.production_lcost ? 'is-invalid' : ''}`}
                                    name="production_lcost"
                                    value={form.production_lcost}
                                    onChange={handleChange}
                                />
                                {errors.production_lcost && <div className="text-danger fs-8">{errors.production_lcost}</div>}
                            </div> */}

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Raw Material Count<span className="required">*</span></label>
                                <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => e.key === '-' && e.preventDefault()} // ⛔ block minus key
                                    className={`form-control ${errors.production_rmcount ? 'is-invalid' : ''}`}
                                    name="production_rmcount"
                                    value={form.production_rmcount}
                                    onChange={handleChange}
                                />
                                {errors.production_rmcount && <div className="text-danger fs-8">{errors.production_rmcount}</div>}
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Raw Material Amount<span className="required">*</span></label>
                                <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                                    className={`form-control ${errors.production_rmcost ? 'is-invalid' : ''}`}
                                    name="production_rmcost"
                                    value={form.production_rmcost}
                                    onChange={handleChange}
                                />
                                {errors.production_rmcost && <div className="text-danger fs-8">{errors.production_rmcost}</div>}
                            </div>

                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Labour Cost<span className="required">*</span></label>
                                <input
                                    type="number"
                                    min="0"
                                    onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                                    className={`form-control ${errors.production_lcost ? 'is-invalid' : ''}`}
                                    name="production_lcost"
                                    value={form.production_lcost}
                                    onChange={handleChange}
                                />
                                {errors.production_lcost && <div className="text-danger fs-8">{errors.production_lcost}</div>}
                            </div>


                            <div className="col-lg-6 mb-2">
                                <label className="text-black font-w500 fs-7">Total Cost</label>
                                <input type="text" className="form-control" readOnly value={totalCost} />
                            </div>
                        </div>
                    )}

                    <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-4">
                        <Button className="btn btn-sm btn-primary" onClick={handleSubmit} disabled={loading}>
                            Update Production Cost <i className="fas fa-angle-double-right ms-2"></i>
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default EditProductionCosting;
